import { useState, useRef, useCallback } from "react";
import { StandardModal } from "@/components/StandardModal";
import { ModalPrimaryAction } from "@/components/ModalPrimaryAction";
import { Select } from "@/components/ui/select";
import { MODAL_BODY_CLASS, MODAL_PANEL_MAX_HEIGHT_CLASS, TABLE_HEAD_CLASS, TABLE_TH_CLASS } from "@/lib/modalStyles";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { api } from "@/lib/api";
import { FileUp, Loader2 } from "lucide-react";
import { toast } from "@/lib/appToast";

/** Limite para não travar o navegador em notebooks fracos (OFX costuma ser pequeno). */
const MAX_OFX_BYTES = 10 * 1024 * 1024;

function validateOfxFile(file: File): string | null {
  const ext = file.name.toLowerCase().split(".").pop();
  if (ext !== "ofx") {
    return "Apenas arquivos com extensão .ofx são aceitos.";
  }
  if (file.size > MAX_OFX_BYTES) {
    return `Arquivo muito grande (máximo ${Math.round(MAX_OFX_BYTES / (1024 * 1024))} MB).`;
  }
  if (file.size === 0) {
    return "O arquivo está vazio.";
  }
  return null;
}

function formatBRL(v: number): string {
  return new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v);
}

function formatDataIsoBr(iso: string): string {
  const p = iso.trim().split("-");
  if (p.length !== 3) return iso;
  return `${p[2]}/${p[1]}/${p[0]}`;
}

type ItemPreview = {
  fitid: string;
  data: string;
  valor: number;
  descricao: string;
  duplicado: boolean;
  saida: "" | "despesa" | "compra" | "outro";
};

export function ModalImportarOfxFinanceiro({
  open,
  onClose,
  onSuccess,
  defaultSaida,
}: {
  open: boolean;
  onClose: () => void;
  onSuccess: () => void;
  defaultSaida: "despesa" | "compra" | "outro";
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const dragDepth = useRef(0);
  const [isDragging, setIsDragging] = useState(false);
  const [step, setStep] = useState<"upload" | "preview">("upload");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [totalEncontrados, setTotalEncontrados] = useState(0);
  const [novos, setNovos] = useState(0);
  const [duplicados, setDuplicados] = useState(0);
  const [itens, setItens] = useState<ItemPreview[]>([]);
  const [selectedFileName, setSelectedFileName] = useState("");

  const reset = useCallback(() => {
    setStep("upload");
    setLoading(false);
    setSaving(false);
    setTotalEncontrados(0);
    setNovos(0);
    setDuplicados(0);
    setItens([]);
    setSelectedFileName("");
    dragDepth.current = 0;
    setIsDragging(false);
    if (fileRef.current) fileRef.current.value = "";
  }, []);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFile = useCallback(async (file: File | null) => {
    if (!file) return;
    const invalid = validateOfxFile(file);
    if (invalid) {
      toast.error(invalid);
      return;
    }
    setSelectedFileName(file.name);
    setLoading(true);
    try {
      const res = await api.previewImportOfx(file);
      setTotalEncontrados(res.total_encontrados);
      setNovos(res.novos);
      setDuplicados(res.duplicados);
      setItens(
        res.itens.map((i) => ({
          ...i,
          descricao: i.descricao,
          saida: i.duplicado ? "" : defaultSaida,
        })),
      );
      setStep("preview");
      toast.success("Extrato OFX carregado com sucesso.");
      if (res.total_encontrados === 0) {
        toast.message(
          "Nenhuma saída encontrada no OFX (OUT/DEBIT ou valor negativo; CREDIT e valores positivos ou zerados são ignorados).",
        );
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Erro ao ler o OFX");
    } finally {
      setLoading(false);
    }
  }, [defaultSaida]);

  const openFilePicker = useCallback(() => {
    if (loading) return;
    fileRef.current?.click();
  }, [loading]);

  const onDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current += 1;
    const types = e.dataTransfer?.types;
    if (types && Array.from(types as unknown as string[]).includes("Files")) {
      setIsDragging(true);
    }
  }, []);

  const onDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragDepth.current = Math.max(0, dragDepth.current - 1);
    if (dragDepth.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const onDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = "copy";
    }
  }, []);

  const onDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragDepth.current = 0;
      setIsDragging(false);
      if (loading) return;
      const f = e.dataTransfer.files?.[0];
      void handleFile(f ?? null);
    },
    [loading, handleFile],
  );

  const updateDescricao = (fitid: string, descricao: string) => {
    setItens((prev) => prev.map((r) => (r.fitid === fitid ? { ...r, descricao } : r)));
  };
  const updateSaida = (fitid: string, saida: "" | "despesa" | "compra" | "outro") => {
    setItens((prev) => prev.map((r) => (r.fitid === fitid ? { ...r, saida } : r)));
  };
  const hasMissingSaida = itens.some((i) => !i.duplicado && i.saida === "");

  const handleSave = async () => {
    const novosItens = itens.filter((i) => !i.duplicado);
    const faltandoSaidaIdx = novosItens
      .map((i, idx) => ({ idx, i }))
      .filter(({ i }) => i.saida === "")
      .map(({ idx }) => idx + 1);
    if (faltandoSaidaIdx.length > 0) {
      toast.error(`Selecione a coluna Saída para todos os lançamentos novos. Linhas: ${faltandoSaidaIdx.join(", ")}`);
      return;
    }
    const toSave = novosItens
      .filter((i) => i.descricao.trim() !== "")
      .map((i) => ({
        fitid: i.fitid,
        nome: i.descricao.trim(),
        valor: i.valor,
        data: i.data,
        saida: i.saida as "despesa" | "compra" | "outro",
      }));
    if (toSave.length === 0) {
      toast.error("Não há lançamentos novos para importar (todos já existem ou descrição vazia).");
      return;
    }
    setSaving(true);
    try {
      const res = await api.saveImportOfx(toSave);
      const salvos = Number(res.salvos ?? 0);
      const ignorados = Number(res.ignorados_duplicata ?? 0);
      if (salvos === 0) {
        const detalhe = res.erros.length > 0 ? ` ${res.erros[0]}` : "";
        toast.error(`Nenhum lançamento foi importado.${detalhe}`);
        return;
      }
      if (res.erros.length > 0) {
        toast.message(`Alguns itens não foram importados. ${res.erros[0]}`);
      }
      toast.success(
        `Importados ${salvos} lançamento(s).` +
          (ignorados > 0 ? ` ${ignorados} ignorado(s) por duplicidade.` : ""),
      );
      onSuccess();
      handleClose();
    } catch (e) {
      console.error("Erro no saveImportOfx:", e);
      toast.error(e instanceof Error ? e.message : "Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  return (
    <StandardModal
      open={open}
      title="Subir extrato OFX"
      titleId="ofx-import-title"
      onClose={handleClose}
      panelClassName={
        step === "upload"
          ? `!max-w-xl ${MODAL_PANEL_MAX_HEIGHT_CLASS} !overflow-hidden`
          : `!max-w-3xl ${MODAL_PANEL_MAX_HEIGHT_CLASS} !overflow-hidden`
      }
      closeDisabled={loading || saving}
    >
      <div className={`${MODAL_BODY_CLASS} min-h-0 flex-1 overflow-y-auto overflow-x-hidden`}>
        {step === "upload" && (
          <div className="space-y-4">
            <input
              id="ofx-file"
              ref={fileRef}
              type="file"
              accept=".ofx"
              className="sr-only"
              tabIndex={-1}
              onChange={(e) => {
                const f = e.target.files?.[0];
                void handleFile(f ?? null);
                e.target.value = "";
              }}
            />
            <div
              role="button"
              tabIndex={0}
              onClick={(ev) => {
                ev.stopPropagation();
                openFilePicker();
              }}
              onKeyDown={(ev) => {
                if (ev.key === "Enter" || ev.key === " ") {
                  ev.preventDefault();
                  openFilePicker();
                }
              }}
              onDragEnter={onDragEnter}
              onDragLeave={onDragLeave}
              onDragOver={onDragOver}
              onDrop={onDrop}
              className={`relative flex min-h-[176px] flex-col items-center justify-center gap-3 rounded-xl border-2 border-dashed px-4 py-8 text-center transition-colors outline-none focus:outline-none focus-visible:outline-none ${
                loading
                  ? "pointer-events-none cursor-wait border-gray-200 bg-gray-50 text-gray-500"
                  : isDragging
                    ? "cursor-copy border-client-primary bg-client-secondary text-gray-900"
                    : "cursor-pointer border-gray-200 bg-gray-50/90 hover:border-gray-300 hover:bg-gray-50"
              }`}
              aria-busy={loading}
              aria-label="Enviar extrato OFX: arraste o arquivo aqui ou clique para selecionar. Somente arquivos .ofx, até 10 megabytes."
            >
              {loading ? (
                <>
                  <Loader2 className="h-10 w-10 shrink-0 animate-spin text-client-primary" aria-hidden />
                  <p className="text-sm font-medium text-gray-800">Lendo extrato…</p>
                  {selectedFileName ? (
                    <p className="max-w-full break-all text-xs text-gray-600">{selectedFileName}</p>
                  ) : null}
                </>
              ) : (
                <>
                  <FileUp className="h-10 w-10 shrink-0 text-gray-400" strokeWidth={1.25} aria-hidden />
                  <div className="space-y-1">
                    <p className="text-sm font-medium text-gray-800">Arraste o arquivo OFX aqui</p>
                    <p className="text-sm text-gray-500">ou clique para selecionar</p>
                  </div>
                  {selectedFileName ? (
                    <p className="max-w-full break-all pt-1 text-xs font-medium text-gray-700">{selectedFileName}</p>
                  ) : null}
                  <p className="text-xs text-gray-400">
                    Apenas .ofx · até {Math.round(MAX_OFX_BYTES / (1024 * 1024))} MB
                  </p>
                </>
              )}
            </div>
          </div>
        )}

        {step === "preview" && (
          <div className="space-y-4">
            <div className="flex flex-wrap gap-4 text-sm rounded-lg border border-gray-100 bg-gray-50 px-3 py-2">
              <span>
                <strong>{totalEncontrados}</strong> lançamentos encontrados
              </span>
              <span className="text-green-700">
                <strong>{novos}</strong> novos
              </span>
              <span className="text-amber-800">
                <strong>{duplicados}</strong> ignorados (já existente)
              </span>
            </div>

            {itens.length === 0 ? (
              <p className="text-muted-foreground text-center py-6">Nenhuma transação de saída para importar.</p>
            ) : (
              <div className="overflow-x-auto rounded-md border border-gray-200">
                <table className="gs-management-table gs-management-table-specialized w-full min-w-[640px] text-sm">
                  <thead className={TABLE_HEAD_CLASS}>
                    <tr>
                      <th className={`whitespace-nowrap text-left py-2 px-2 ${TABLE_TH_CLASS}`}>Data</th>
                      <th className={`whitespace-nowrap text-left py-2 px-2 ${TABLE_TH_CLASS}`}>Descrição</th>
                      <th className={`whitespace-nowrap text-left py-2 px-2 ${TABLE_TH_CLASS}`}>Saída</th>
                      <th className={`whitespace-nowrap text-left py-2 px-2 ${TABLE_TH_CLASS}`}>Valor</th>
                      <th className={`whitespace-nowrap text-left py-2 px-2 w-28 ${TABLE_TH_CLASS}`}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {itens.map((row) => (
                      <tr key={row.fitid} className={`border-t border-gray-100 ${row.duplicado ? "opacity-60 bg-muted/20" : ""}`}>
                        <td className="py-2 px-2 text-xs whitespace-nowrap">{formatDataIsoBr(row.data)}</td>
                        <td className="py-2 px-2 min-w-[200px]">
                          <Input
                            value={row.descricao}
                            disabled={row.duplicado}
                            onChange={(e) => updateDescricao(row.fitid, e.target.value)}
                            className="h-8 text-sm"
                          />
                        </td>
                        <td className="py-2 px-2 min-w-[140px]">
                          <Select
                            value={row.saida}
                            disabled={row.duplicado}
                            onChange={(e) => updateSaida(row.fitid, e.target.value as "" | "despesa" | "compra" | "outro")}
                            className="h-8 w-full px-2 text-sm"
                          >
                            <option value="">Selecione</option>
                            <option value="despesa">Despesa</option>
                            <option value="compra">Compras</option>
                            <option value="outro">Outros</option>
                          </Select>
                        </td>
                        <td className="py-2 px-2 whitespace-nowrap">{formatBRL(row.valor)}</td>
                        <td className="py-2 px-2 text-xs">
                          {row.duplicado ? "Já importado" : "Novo"}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex flex-wrap justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => { reset(); }} disabled={saving}>
                Outro arquivo
              </Button>
            </div>
            <ModalPrimaryAction
              show={novos > 0 && !hasMissingSaida}
              type="button"
              loading={saving}
              disabled={saving || novos === 0 || hasMissingSaida}
              onClick={() => void handleSave()}
            >
              Importar lançamentos
            </ModalPrimaryAction>
          </div>
        )}
      </div>
    </StandardModal>
  );
}
